import React from 'react';

import logoPreta from '../assets/LOGO_PRETA.png';
import { formatarDataHoraAtual } from '../mapeamento/formatadoresDanfe';
import {
  ALTURA_BANDA,
  CABECALHO,
  CANHOTO,
  COLUNAS_PRODUTOS,
  COLUNA_ALIQUOTAS,
  DESTINATARIO,
  FATURA,
  FUNDO_DESTAQUE,
  MARGENS,
  PRODUTOS,
  RECUO,
  RODAPE,
  TIPOGRAFIA,
  calculoImposto,
  topoCabecalho,
  transporte,
} from './grade';
import { ENTRELINHA_NATURAL, truncarParaLargura } from './metricaTexto';
import { Campo, Imagem, LinhaDeRecorte, Quadro, Texto } from './primitivos';

/**
 * @file ./views/layouts/danfe/producao/blocos.jsx
 *
 * Os quadros do DANFE, na ordem e nas coordenadas da grade herdada do
 * gerador `pdfkit`. Cada componente devolve um fragmento de elementos
 * posicionados em absoluto — **nunca** um `<View>` de agrupamento.
 *
 * O motivo é o Yoga: ele posiciona um filho absoluto em relação ao **pai
 * direto**, e não em relação ao ancestral posicionado mais próximo, como
 * faria o CSS. Envolver estes blocos num contêiner deslocaria todas as
 * coordenadas para dentro dele. Por isso tudo aqui precisa ser filho direto
 * da `<Page>`.
 *
 * Nenhuma regra fiscal mora neste arquivo: os valores chegam do
 * `danfeMapper` já mascarados, formatados e em caixa alta.
 */

/**
 * Canhoto destacável e fio de recorte — apenas na primeira folha (MOC 3.3.1
 * permite suprimi-lo).
 *
 * @param {Object} props
 * @param {Object} props.danfe - DTO do `danfeMapper`.
 * @returns {React.ReactElement}
 */
export function Canhoto({ danfe }) {
  const { emitente, destinatario, documento, impostos } = danfe;

  // A 6 pt a declaração ultrapassa a largura da caixa quando a razão
  // social é longa; cortar mantém a linha única que a banda comporta.
  const declaracao = truncarParaLargura(
    `RECEBEMOS DE ${emitente.nome} OS PRODUTOS / SERVIÇOS CONSTANTES DA NOTA FISCAL INDICADA AO LADO`,
    CANHOTO.recebemos.largura,
    CANHOTO.recebemos.corpo,
  );
  const resumo = truncarParaLargura(
    `EMISSÃO: ${documento.dataEmissao} - DEST. / REM.: ${destinatario.nome}` +
      ` - VALOR TOTAL: R$ ${impostos.valorTotalNota}`,
    CANHOTO.emissao.largura,
    CANHOTO.emissao.corpo,
  );

  return (
    <>
      <Quadro {...CANHOTO.declaracao} />
      <Texto {...CANHOTO.recebemos}>{declaracao}</Texto>
      <Texto {...CANHOTO.emissao}>{resumo}</Texto>

      <Quadro {...CANHOTO.caixaNfe} />
      <Texto {...CANHOTO.tituloNfe}>NF-e</Texto>
      <Texto {...CANHOTO.numeroNfe}>{`Nº ${documento.numero}`}</Texto>
      <Texto {...CANHOTO.serieNfe}>{`SÉRIE ${documento.serie}`}</Texto>

      <Quadro {...CANHOTO.caixaData} />
      <Texto {...CANHOTO.rotuloData}>DATA DE RECEBIMENTO</Texto>

      <Quadro {...CANHOTO.caixaAssinatura} />
      <Texto {...CANHOTO.rotuloAssinatura}>
        IDENTIFICAÇÃO E ASSINATURA DO RECEBEDOR
      </Texto>

      <LinhaDeRecorte {...CANHOTO.recorte} />
    </>
  );
}

/**
 * Desloca uma entrada da grade do cabeçalho (que guarda `dy` relativo ao
 * topo do quadro) para a coordenada absoluta da folha.
 *
 * @param {Object} entrada - Objeto com `dy`.
 * @param {number} topo - Topo do quadro de identificação nesta folha.
 * @returns {Object} Cópia com `y` absoluto e sem `dy`.
 */
function emY(entrada, topo) {
  const { dy, ...resto } = entrada;
  return { ...resto, y: topo + dy };
}

/** Aplica {@link emY} às três partes de um campo da grade do cabeçalho. */
function campoEmY(entrada, topo) {
  return {
    caixa: emY(entrada.caixa, topo),
    rotulo: emY(entrada.rotulo, topo),
    valor: emY(entrada.valor, topo),
  };
}

/** Bloco do emitente: logo, razão social e endereço. */
function BlocoEmitente({ emitente, topo }) {
  const { emitente: gradeEmit } = CABECALHO;

  // Razão social tem linha única fixa: truncar com reticências (como o
  // bairro do Destinatário) evita meio caractere cortado no fio
  // inferior. O endereço, mais sujeito a estourar uma linha, quebra
  // naturalmente dentro da altura reservada em vez de truncar.
  const nomeEmitente = truncarParaLargura(
    emitente.nome,
    gradeEmit.nome.largura,
    gradeEmit.nome.corpo,
    gradeEmit.nome.negrito,
  );
  const enderecoEmitente = [
    `${emitente.endereco.logradouro}, ${emitente.endereco.numero}`,
    emitente.endereco.bairro,
    `${emitente.endereco.municipio}/${emitente.endereco.uf}`,
    emitente.endereco.cep,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <Quadro {...emY(gradeEmit.caixa, topo)} />
      <Texto {...emY(gradeEmit.rotulo, topo)}>IDENTIFICAÇÃO DO EMITENTE</Texto>
      <Imagem src={logoPreta} {...emY(gradeEmit.imagem, topo)} />
      <Texto {...emY(gradeEmit.nome, topo)}>{nomeEmitente}</Texto>
      <Texto {...emY(gradeEmit.endereco, topo)}>{enderecoEmitente}</Texto>
    </>
  );
}

/** Bloco "DANFE": título, tipo de operação, número/série/folha. */
function BlocoDanfe({ documento, folha, topo }) {
  const { danfe: gradeDanfe } = CABECALHO;

  return (
    <>
      <Quadro {...emY(gradeDanfe.caixa, topo)} />
      <Texto {...emY(gradeDanfe.titulo, topo)}>DANFE</Texto>
      <Texto {...emY(gradeDanfe.subtitulo, topo)}>
        DOCUMENTO AUXILIAR DA NOTA FISCAL ELETRÔNICA
      </Texto>
      <Texto {...emY(gradeDanfe.entrada, topo)}>0 - ENTRADA</Texto>
      <Texto {...emY(gradeDanfe.saida, topo)}>1 - SAÍDA</Texto>
      <Quadro {...emY(gradeDanfe.caixaTipo, topo)} />
      <Texto {...emY(gradeDanfe.tipo, topo)}>
        {documento.tipoOperacao.codigo}
      </Texto>
      <Texto
        {...emY(gradeDanfe.numero, topo)}
      >{`Nº ${documento.numero}`}</Texto>
      <Texto
        {...emY(gradeDanfe.serie, topo)}
      >{`SÉRIE ${documento.serie}`}</Texto>
      <Texto
        {...emY(gradeDanfe.folha, topo)}
      >{`FOLHA ${folha.numero}/${folha.total}`}</Texto>
    </>
  );
}

/**
 * Bloco da chave de acesso: código de barras, chave formatada e a faixa de
 * consulta/aviso (autenticidade, contingência ou nada, conforme o estado
 * do documento).
 */
function BlocoChaveAcesso({
  chave,
  documento,
  codigoBarras,
  exibirAutorizacao,
  topo,
}) {
  const { chave: gradeChave } = CABECALHO;

  return (
    <>
      <Quadro {...emY(gradeChave.caixaBarras, topo)} />
      {codigoBarras ? (
        <Imagem src={codigoBarras.src} {...emY(gradeChave.barras, topo)} />
      ) : (
        <Texto {...emY(gradeChave.avisoNaoEnviada, topo)} corpo={10}>
          ERRO AO GERAR CÓDIGO DE BARRAS
        </Texto>
      )}
      <Quadro {...emY(gradeChave.caixaChave, topo)} />
      <Texto {...emY(gradeChave.rotuloChave, topo)}>CHAVE DE ACESSO</Texto>
      <Texto {...emY(gradeChave.valorChave, topo)}>{chave.formatada}</Texto>

      {/* MOC 3.9.2 — em contingência, substitui a consulta de
                autenticidade pelo aviso de contingência, independente de a
                nota já ter protocolo (o tpEmis é um fato histórico da
                emissão, não muda quando a SEFAZ autoriza depois). */}
      <Quadro {...emY(gradeChave.caixaConsulta, topo)} />
      {documento.contingencia ? (
        <>
          <Texto {...emY(gradeChave.consulta[0], topo)} negrito>
            DANFE EM CONTINGÊNCIA
          </Texto>
          <Texto {...emY(gradeChave.consulta[1], topo)}>SVC-RS</Texto>
          <Texto {...emY(gradeChave.consulta[2], topo)}>
            Consulte a autorização de uso no portal da SEFAZ Virtual de
            Contingência
          </Texto>
        </>
      ) : exibirAutorizacao ? (
        <>
          <Texto {...emY(gradeChave.consulta[0], topo)}>
            Consulta de autenticidade no portal nacional da NF-e
          </Texto>
          <Texto {...emY(gradeChave.consulta[1], topo)}>
            www.nfe.fazenda.gov.br/portal
          </Texto>
          <Texto {...emY(gradeChave.consulta[2], topo)}>
            ou no site da Sefaz Autorizadora
          </Texto>
        </>
      ) : null}
    </>
  );
}

/** Bloco de natureza da operação e protocolo de autorização. */
function BlocoNaturezaProtocolo({ documento, exibirAutorizacao, topo }) {
  return (
    <>
      <Quadro {...emY(CABECALHO.natureza.caixa, topo)} />
      <Texto {...emY(CABECALHO.natureza.rotulo, topo)}>
        NATUREZA DA OPERAÇÃO
      </Texto>
      <Texto {...emY(CABECALHO.natureza.valor, topo)}>
        {documento.naturezaOperacao}
      </Texto>

      <Quadro {...emY(CABECALHO.protocolo.caixa, topo)} />
      {exibirAutorizacao ? (
        <>
          <Texto {...emY(CABECALHO.protocolo.rotulo, topo)}>
            PROTOCOLO DE AUTORIZAÇÃO DE USO
          </Texto>
          <Texto {...emY(CABECALHO.protocolo.valor, topo)}>
            {documento.protocolo}
          </Texto>
        </>
      ) : null}
    </>
  );
}

/** Bloco das três inscrições do emitente (estadual, do substituto tributário e CNPJ/CPF). */
function BlocoInscricoes({ emitente, topo }) {
  return (
    <>
      <Campo
        grade={campoEmY(CABECALHO.inscricoes[0], topo)}
        rotulo="INSCRIÇÃO ESTADUAL"
        valor={emitente.inscricaoEstadual}
      />
      <Campo
        grade={campoEmY(CABECALHO.inscricoes[1], topo)}
        rotulo="INSCRIÇÃO ESTADUAL DO SUBST. TRIB."
        valor={emitente.inscricaoEstadualSubstituto}
      />
      <Campo
        grade={campoEmY(CABECALHO.inscricoes[2], topo)}
        rotulo="CNPJ / CPF"
        valor={emitente.documento}
      />
    </>
  );
}

/**
 * Quadro de identificação: emitente, bloco DANFE, código de barras e chave,
 * natureza da operação, protocolo e inscrições.
 *
 * É o bloco que o MOC 3.5 obriga a repetir em toda folha adicional, junto
 * com o contador de folhas que o item 3.10.2 exige mesmo em documento de
 * folha única — daí receber `folha`. Composto de cinco sub-blocos
 * (emitente, DANFE, chave de acesso, natureza/protocolo, inscrições), cada
 * um filho direto deste componente — e, por sua vez, filho direto da
 * `<Page>` (ver a nota do Yoga no topo do arquivo).
 *
 * @param {Object} props
 * @param {Object} props.danfe
 * @param {{ numero: number, total: number }} props.folha
 * @param {{ src: string }|null} props.codigoBarras
 * @param {boolean} props.primeiraFolha
 * @returns {React.ReactElement}
 */
export function Cabecalho({ danfe, folha, codigoBarras, primeiraFolha }) {
  const topo = topoCabecalho(primeiraFolha);
  const { emitente, documento, chave, ambiente } = danfe;

  // O protocolo e a instrução de consulta só fazem sentido num documento
  // que existe na SEFAZ — ou numa nota de homologação, que é encenação.
  const exibirAutorizacao = ambiente.homologacao || documento.autorizada;

  return (
    <>
      <BlocoEmitente emitente={emitente} topo={topo} />
      <BlocoDanfe documento={documento} folha={folha} topo={topo} />
      <BlocoChaveAcesso
        chave={chave}
        documento={documento}
        codigoBarras={codigoBarras}
        exibirAutorizacao={exibirAutorizacao}
        topo={topo}
      />
      <BlocoNaturezaProtocolo
        documento={documento}
        exibirAutorizacao={exibirAutorizacao}
        topo={topo}
      />
      <BlocoInscricoes emitente={emitente} topo={topo} />
    </>
  );
}

/**
 * Quadro do destinatário/remetente — apenas na primeira folha.
 *
 * @param {Object} props
 * @param {Object} props.danfe
 * @returns {React.ReactElement}
 */
export function Destinatario({ danfe }) {
  const { destinatario, documento } = danfe;
  const { bairro } = DESTINATARIO;

  return (
    <>
      <Texto {...DESTINATARIO.titulo}>DESTINATÁRIO / REMETENTE</Texto>

      <Campo
        grade={DESTINATARIO.nome}
        rotulo="NOME / RAZÃO SOCIAL"
        valor={destinatario.nome}
      />
      <Campo
        grade={DESTINATARIO.documento}
        rotulo="CNPJ / CPF"
        valor={destinatario.documento}
      />
      <Campo
        grade={DESTINATARIO.dataEmissao}
        rotulo="DATA DA EMISSÃO"
        valor={documento.dataEmissao}
      />

      <Campo
        grade={DESTINATARIO.endereco}
        rotulo="ENDEREÇO"
        valor={`${destinatario.endereco.logradouro}, ${destinatario.endereco.numero}`}
      />

      {/* Bairros longos são cortados em vez de quebrar linha — a banda
                de 23 pt só comporta uma linha de conteúdo. */}
      <Campo grade={bairro} rotulo="BAIRRO / DISTRITO">
        <Texto {...bairro.valor}>
          {truncarParaLargura(
            destinatario.endereco.bairro,
            bairro.valor.largura,
            bairro.valor.corpo,
            bairro.valor.negrito,
          )}
        </Texto>
      </Campo>

      <Campo
        grade={DESTINATARIO.cep}
        rotulo="CEP"
        valor={destinatario.endereco.cep}
      />
      <Campo
        grade={DESTINATARIO.dataSaida}
        rotulo="SAÍDA / ENTRADA"
        valor={documento.dataSaidaEntrada}
      />

      <Campo
        grade={DESTINATARIO.municipio}
        rotulo="MUNICÍPIO"
        valor={destinatario.endereco.municipio}
      />
      <Campo
        grade={DESTINATARIO.telefone}
        rotulo="FONE / FAX"
        valor={destinatario.endereco.telefone}
      />
      <Campo
        grade={DESTINATARIO.uf}
        rotulo="UF"
        valor={destinatario.endereco.uf}
      />
      <Campo
        grade={DESTINATARIO.inscricaoEstadual}
        rotulo="INSCRIÇÃO ESTADUAL"
        valor={destinatario.inscricaoEstadual}
      />
      <Campo
        grade={DESTINATARIO.horaSaida}
        rotulo="HORA DA SAÍDA"
        valor={documento.horaSaidaEntrada}
      />
    </>
  );
}

/**
 * Quadro de fatura/duplicatas — o único de largura elástica: as células
 * dividem igualmente a largura útil, até sete por banda, e a última banda
 * fica com células mais largas quando não completa a linha.
 *
 * @param {Object} props
 * @param {Object} props.danfe
 * @returns {React.ReactElement|null} `null` quando a nota não tem duplicatas.
 */
export function Fatura({ danfe }) {
  const { duplicatas } = danfe.cobranca;
  if (duplicatas.length === 0) return null;

  const bandas = [];
  for (let inicio = 0; inicio < duplicatas.length; inicio += FATURA.porBanda) {
    bandas.push(duplicatas.slice(inicio, inicio + FATURA.porBanda));
  }

  return (
    <>
      <Texto {...FATURA.titulo}>FATURA / DUPLICATAS</Texto>

      {bandas.map((banda, indiceBanda) => {
        const larguraCelula = FATURA.larguraTotal / banda.length;
        const y = FATURA.topoBandas + indiceBanda * FATURA.alturaBanda;
        const larguraTexto = larguraCelula - FATURA.recuoCelula * 2;

        return banda.map((duplicata, indiceCelula) => {
          const x = MARGENS.esquerda + indiceCelula * larguraCelula;
          const conteudo = [
            `NÚM. ${duplicata.numero}`,
            `VENC. ${duplicata.vencimento}`,
            `VALOR ${duplicata.valor}`,
          ];

          return (
            <React.Fragment key={`dup-${indiceBanda}-${indiceCelula}`}>
              <Quadro
                x={x}
                y={y}
                largura={larguraCelula}
                altura={FATURA.alturaBanda}
              />
              {conteudo.map((linha, indiceLinha) => (
                <Texto
                  key={`dup-${indiceBanda}-${indiceCelula}-${indiceLinha}`}
                  {...FATURA.linhas[indiceLinha]}
                  x={x + FATURA.recuoCelula}
                  y={y + FATURA.linhas[indiceLinha].dy}
                  largura={larguraTexto}
                >
                  {truncarParaLargura(
                    linha,
                    larguraTexto,
                    FATURA.linhas[indiceLinha].corpo,
                  )}
                </Texto>
              ))}
            </React.Fragment>
          );
        });
      })}
    </>
  );
}

/**
 * Quadro de cálculo do imposto. As duas células de totalização fecham com
 * fundo cinza, como no original.
 *
 * @param {Object} props
 * @param {Object} props.danfe
 * @param {number} props.deslocamento - Altura consumida pelo quadro de fatura.
 * @returns {React.ReactElement}
 */
export function CalculoImposto({ danfe, deslocamento }) {
  const grade = calculoImposto(deslocamento);
  const { impostos } = danfe;

  return (
    <>
      <Texto {...grade.titulo}>CÁLCULO DO IMPOSTO</Texto>

      <Campo
        grade={grade.baseCalculoIcms}
        rotulo="BASE DE CÁLCULO DO ICMS"
        valor={impostos.baseCalculoIcms}
      />
      <Campo
        grade={grade.valorIcms}
        rotulo="VALOR DO ICMS"
        valor={impostos.valorIcms}
      />
      <Campo
        grade={grade.baseCalculoIcmsSt}
        rotulo="B. CÁLC. ICMS ST"
        valor={impostos.baseCalculoIcmsSt}
      />
      <Campo
        grade={grade.valorIcmsSt}
        rotulo="VALOR DO ICMS ST"
        valor={impostos.valorIcmsSt}
      />
      <Campo
        grade={grade.valorAproximadoTributos}
        rotulo="VALOR APROX. TRIBUTOS"
        valor={impostos.valorAproximadoTributos}
      />
      {/* Fora do quadro sugerido pelo MOC 7.0 (item 3.8.1) — acrescentado a pedido do usuário. */}
      <Campo
        grade={grade.valorPis}
        rotulo="VALOR DO PIS"
        valor={impostos.valorPis}
      />
      <Campo
        grade={grade.valorProdutos}
        rotulo="VALOR TOTAL DOS PRODUTOS"
        valor={impostos.valorProdutos}
      />

      <Campo
        grade={grade.valorFrete}
        rotulo="VALOR DO FRETE"
        valor={impostos.valorFrete}
      />
      <Campo
        grade={grade.valorSeguro}
        rotulo="VALOR DO SEGURO"
        valor={impostos.valorSeguro}
      />
      <Campo
        grade={grade.valorDesconto}
        rotulo="DESCONTO"
        valor={impostos.valorDesconto}
      />
      <Campo
        grade={grade.valorOutrasDespesas}
        rotulo="OUTRAS DESP. ACESS."
        valor={impostos.valorOutrasDespesas}
      />
      <Campo
        grade={grade.valorIpi}
        rotulo="VALOR TOTAL DO IPI"
        valor={impostos.valorIpi}
      />
      <Campo
        grade={grade.valorCofins}
        rotulo="VALOR DO COFINS"
        valor={impostos.valorCofins}
      />
      <Campo
        grade={grade.valorTotalNota}
        rotulo="VALOR TOTAL DA NOTA"
        valor={impostos.valorTotalNota}
      />
    </>
  );
}

/**
 * Quadro do transportador e dos volumes. Cada volume declarado no XML gera
 * uma banda adicional.
 *
 * @param {Object} props
 * @param {Object} props.danfe
 * @param {number} props.deslocamento
 * @returns {React.ReactElement}
 */
export function Transporte({ danfe, deslocamento }) {
  const grade = transporte(deslocamento);
  const {
    transportadora,
    veiculo,
    volumes,
    modalidadeFrete,
  } = danfe.transporte;

  return (
    <>
      <Texto {...grade.titulo}>TRANSPORTADOR / VOLUMES TRANSPORTADOS</Texto>

      <Campo
        grade={grade.nome}
        rotulo="RAZÃO SOCIAL"
        valor={transportadora.nome}
      />
      <Campo
        grade={grade.modalidadeFrete}
        rotulo="FRETE POR CONTA"
        valor={modalidadeFrete}
      />
      <Campo
        grade={grade.codigoAntt}
        rotulo="CÓDIGO ANTT"
        valor={veiculo.codigoAntt}
      />
      <Campo grade={grade.placa} rotulo="PLACA VEÍCULO" valor={veiculo.placa} />
      <Campo grade={grade.ufVeiculo} rotulo="UF" valor={veiculo.uf} />
      <Campo
        grade={grade.documento}
        rotulo="CNPJ / CPF"
        valor={transportadora.documento}
      />

      <Campo
        grade={grade.endereco}
        rotulo="ENDEREÇO"
        valor={transportadora.endereco}
      />
      <Campo
        grade={grade.municipio}
        rotulo="MUNICÍPIO"
        valor={transportadora.municipio}
      />
      <Campo grade={grade.uf} rotulo="UF" valor={transportadora.uf} />
      <Campo
        grade={grade.inscricaoEstadual}
        rotulo="INSCRIÇÃO ESTADUAL"
        valor={transportadora.inscricaoEstadual}
      />

      {volumes.map((volume, indice) => {
        const y = grade.volumes.topo + indice * grade.volumes.passo;

        return (
          <React.Fragment key={`volume-${indice}`}>
            {grade.volumes.colunas.map((coluna) => (
              <React.Fragment key={`volume-${indice}-${coluna.chave}`}>
                <Quadro
                  x={coluna.x}
                  y={y}
                  largura={coluna.largura}
                  altura={ALTURA_BANDA}
                />
                <Texto
                  x={coluna.x + 4}
                  y={y + RECUO.rotulo}
                  largura={coluna.largura - 6}
                  corpo={TIPOGRAFIA.rotuloCampo}
                  espacamento={0.5}
                >
                  {coluna.rotulo}
                </Texto>
                <Texto
                  x={coluna.x + (coluna.alinhamento === 'right' ? 0 : 5)}
                  y={y + RECUO.valor}
                  largura={coluna.largura - 4}
                  corpo={TIPOGRAFIA.conteudoCampo}
                  espacamento={0.5}
                  alinhamento={coluna.alinhamento}
                >
                  {volume[coluna.chave]}
                </Texto>
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

/**
 * Linha de base do rótulo de uma coluna de produtos: centraliza o texto
 * vertical na altura do cabeçalho, conforme ele ocupe uma ou duas linhas.
 *
 * @param {number} linhas
 * @returns {number}
 */
function recuoRotuloProduto(linhas) {
  const alturaTexto = linhas * TIPOGRAFIA.rotuloProduto * ENTRELINHA_NATURAL;
  return Math.max(0.5, (PRODUTOS.alturaCabecalho - alturaTexto) / 2);
}

/**
 * Cabeçalho da tabela de produtos: 15 colunas simples mais a coluna de
 * alíquotas, que tem rótulo em dois níveis.
 *
 * @param {Object} props
 * @param {number} props.topo
 * @returns {React.ReactElement}
 */
function CabecalhoProdutos({ topo }) {
  const { alturaCabecalho, recuoHorizontal } = PRODUTOS;

  return (
    <>
      {COLUNAS_PRODUTOS.map((coluna) => (
        <React.Fragment key={`cab-${coluna.chave}`}>
          <Quadro
            x={coluna.x}
            y={topo}
            largura={coluna.largura}
            altura={alturaCabecalho}
            fundo={FUNDO_DESTAQUE}
          />
          <Texto
            x={coluna.x + recuoHorizontal}
            y={topo + recuoRotuloProduto(coluna.linhasRotulo)}
            largura={coluna.largura - recuoHorizontal * 2}
            corpo={TIPOGRAFIA.rotuloProduto}
            alinhamento="center"
          >
            {coluna.rotulo}
          </Texto>
        </React.Fragment>
      ))}

      <Quadro
        x={COLUNA_ALIQUOTAS.grupo.x}
        y={topo}
        largura={COLUNA_ALIQUOTAS.grupo.largura}
        altura={COLUNA_ALIQUOTAS.grupo.altura}
        fundo={FUNDO_DESTAQUE}
      />
      <Texto
        x={COLUNA_ALIQUOTAS.grupo.x}
        y={topo + 1}
        largura={COLUNA_ALIQUOTAS.grupo.largura}
        corpo={TIPOGRAFIA.rotuloProduto}
        alinhamento="center"
      >
        {COLUNA_ALIQUOTAS.grupo.rotulo}
      </Texto>

      {COLUNA_ALIQUOTAS.subcolunas.map((subcoluna) => (
        <React.Fragment key={`cab-${subcoluna.chave}`}>
          <Quadro
            x={subcoluna.x}
            y={topo + COLUNA_ALIQUOTAS.grupo.altura}
            largura={subcoluna.largura}
            altura={COLUNA_ALIQUOTAS.alturaSubcabecalho}
            fundo={FUNDO_DESTAQUE}
          />
          <Texto
            x={subcoluna.x}
            y={topo + COLUNA_ALIQUOTAS.grupo.altura + 1}
            largura={subcoluna.largura}
            corpo={TIPOGRAFIA.rotuloProduto}
            alinhamento="center"
          >
            {subcoluna.rotulo}
          </Texto>
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * Quadro "Dados dos Produtos/Serviços". As posições e alturas das linhas
 * chegam prontas do motor de paginação — este componente só desenha.
 *
 * A moldura de cada célula cumpre o destaque divisório que o MOC 3.1.7
 * exige entre itens que ocupam mais de uma linha.
 *
 * @param {Object} props
 * @param {Array<{ item: Object, y: number, altura: number }>} props.linhas
 * @param {number} props.topoTitulo
 * @param {number} props.topoTabela
 * @returns {React.ReactElement}
 */
export function Produtos({ linhas, topoTitulo, topoTabela }) {
  const { recuoHorizontal, recuoValor } = PRODUTOS;

  return (
    <>
      <Texto
        x={MARGENS.esquerda}
        y={topoTitulo}
        corpo={TIPOGRAFIA.tituloBloco}
        espacamento={0.5}
        negrito
      >
        DADOS DOS PRODUTOS / SERVIÇOS
      </Texto>

      <CabecalhoProdutos topo={topoTabela} />

      {linhas.map(({ item, y, altura }) => (
        <React.Fragment key={`item-${item.numero}`}>
          {COLUNAS_PRODUTOS.map((coluna) => (
            <React.Fragment key={`item-${item.numero}-${coluna.chave}`}>
              <Quadro
                x={coluna.x}
                y={y}
                largura={coluna.largura}
                altura={altura}
              />
              <Texto
                x={coluna.x + recuoHorizontal}
                y={y + recuoValor}
                largura={coluna.largura - recuoHorizontal * 2}
                corpo={TIPOGRAFIA.conteudoProduto}
                alinhamento={coluna.alinhamento}
              >
                {coluna.chave === 'descricao'
                  ? item.descricaoCompleta
                  : item[coluna.chave]}
              </Texto>
            </React.Fragment>
          ))}

          {COLUNA_ALIQUOTAS.subcolunas.map((subcoluna) => (
            <React.Fragment key={`item-${item.numero}-${subcoluna.chave}`}>
              <Quadro
                x={subcoluna.x}
                y={y}
                largura={subcoluna.largura}
                altura={altura}
              />
              <Texto
                x={subcoluna.x + recuoHorizontal}
                y={y + recuoValor}
                largura={subcoluna.largura - recuoHorizontal * 2}
                corpo={TIPOGRAFIA.conteudoProduto}
                alinhamento="right"
              >
                {item[subcoluna.chave]}
              </Texto>
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * Decide o texto da marca d'água do rodapé (MOC 3.9), sempre duas linhas:
 * "SEM VALOR FISCAL" e o motivo. Homologação vem antes de contingência de
 * propósito — é sempre encenação, mesmo que a nota também esteja em
 * contingência. Já a contingência em produção (MOC 3.9.2) não suprime o
 * valor fiscal do documento, então não gera marca d'água por si só; só a
 * ausência de protocolo (nota ainda não autorizada) gera.
 *
 * @param {Object} danfe
 * @returns {{ linha1: string, linha2: string }|null}
 */
function textoMarcaDagua({ ambiente, documento }) {
  if (ambiente.homologacao) {
    return {
      linha1: 'SEM VALOR FISCAL',
      linha2: 'EMITIDO EM AMBIENTE DE HOMOLOGAÇÃO',
    };
  }
  if (documento.contingencia) {
    return null;
  }
  if (!documento.autorizada) {
    return {
      linha1: 'SEM VALOR FISCAL',
      linha2: 'FALTA PROTOCOLO DE APROVAÇÃO DA SEFAZ',
    };
  }
  return null;
}

/**
 * Quadro de dados adicionais, marca d'água e tarja de homologação.
 * Repetido em todas as folhas, como no original.
 *
 * @param {Object} props
 * @param {Object} props.danfe
 * @returns {React.ReactElement}
 */
export function Rodape({ danfe }) {
  const { dadosAdicionais, ambiente, documento } = danfe;
  const marcaDagua = textoMarcaDagua({ ambiente, documento });

  return (
    <>
      <Texto {...RODAPE.titulo}>DADOS ADICIONAIS</Texto>

      <Quadro {...RODAPE.caixaComplementares} />
      <Texto {...RODAPE.rotuloComplementares}>INFORMAÇÕES COMPLEMENTARES</Texto>
      <Texto {...RODAPE.valorComplementares}>
        {dadosAdicionais.informacoesComplementares}
      </Texto>

      <Quadro {...RODAPE.caixaFisco} />
      <Texto {...RODAPE.rotuloFisco}>RESERVADO AO FISCO</Texto>
      <Texto {...RODAPE.valorFisco}>{dadosAdicionais.reservadoFisco}</Texto>

      <Texto
        {...RODAPE.carimboImpressao}
      >{`Impresso: ${formatarDataHoraAtual()}`}</Texto>

      {marcaDagua ? (
        <>
          <Texto
            x={RODAPE.marcaDagua.x}
            y={RODAPE.marcaDagua.y}
            largura={RODAPE.marcaDagua.largura}
            alinhamento={RODAPE.marcaDagua.alinhamento}
            corpo={RODAPE.marcaDagua.corpoDestaque}
            cor={RODAPE.marcaDagua.cor}
            negrito
          >
            {marcaDagua.linha1}
          </Texto>
          <Texto
            x={RODAPE.marcaDagua.x}
            y={
              RODAPE.marcaDagua.y +
              RODAPE.marcaDagua.corpoDestaque * ENTRELINHA_NATURAL
            }
            largura={RODAPE.marcaDagua.largura}
            alinhamento={RODAPE.marcaDagua.alinhamento}
            corpo={RODAPE.marcaDagua.corpoMotivo}
            cor={RODAPE.marcaDagua.cor}
          >
            {marcaDagua.linha2}
          </Texto>
        </>
      ) : null}
    </>
  );
}
